import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AssociateProductModal } from "@/components/scraped-product/associate/modal";
import { DisassociateProductModal } from "@/components/scraped-product/disassociate/modal";
import { ScrapedProductTable } from "@/components/scraped-product/table";
import { useGetScrapedProducts } from "@/hooks/api/scraped-product/useGetScrapedProducts";
import { AppPage } from "@/layouts/page";
import { useDebounce } from "@/hooks/useDebounce";
import { ScrapedProduct } from "@/types/scraped-product";

export const ScrapedProductsPage = () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const initialPage = searchParams.get("page")
    ? parseInt(searchParams.get("page")!)
    : 1;
  const initialLimit = searchParams.get("limit")
    ? parseInt(searchParams.get("limit")!)
    : 10;

  const [pageParams, setPageParams] = useState({
    page: initialPage,
    limit: initialLimit,
  });

  const [nameFilter, setNameFilter] = useState(searchParams.get("name") || "");
  const debouncedNameFilter = useDebounce(nameFilter, 500);
  const [supplierIdFilter, setSupplierIdFilter] = useState(
    searchParams.get("supplierId") || "",
  );
  const [brandFilter, setBrandFilter] = useState(
    searchParams.get("brandFilter") || "",
  );
  const [categoryFilter, setCategoryFilter] = useState(
    searchParams.get("categoryFilter") || "",
  );
  const [stockFilter, setStockFilter] = useState(
    searchParams.get("stockFilter") || "",
  );
  const [blacklistFilter, setBlacklistFilter] = useState(
    searchParams.get("blacklistFilter") || "",
  );
  const [linkedProductStatus, setLinkedProductStatus] = useState(
    searchParams.get("linkedStatus") || "",
  );
  // Parse category IDs and names from URL (format: "id1:name1,id2:name2")
  const parsedCategories = useMemo(
    () =>
      (searchParams.get("categoryIds") || "")
        .split(",")
        .filter(Boolean)
        .map((item) => {
          const [id, name] = item.split(":");

          return id && name ? { id, name } : null;
        })
        .filter((item): item is { id: string; name: string } => item !== null),
    [searchParams],
  );

  const [scrapedCategoryIds, setScrapedCategoryIds] = useState<string[]>(() =>
    (searchParams.get("categoryIds") || "")
      .split(",")
      .filter(Boolean)
      .map((item) => item.split(":")[0])
      .filter(Boolean),
  );

  // Parse brand IDs and names from URL (format: "id1:name1,id2:name2")
  const parsedBrands = useMemo(
    () =>
      (searchParams.get("brandIds") || "")
        .split(",")
        .filter(Boolean)
        .map((item) => {
          const [id, name] = item.split(":");

          return id && name ? { id, name } : null;
        })
        .filter((item): item is { id: string; name: string } => item !== null),
    [searchParams],
  );

  const [scrapedBrandIds, setScrapedBrandIds] = useState<string[]>(() =>
    (searchParams.get("brandIds") || "")
      .split(",")
      .filter(Boolean)
      .map((item) => item.split(":")[0])
      .filter(Boolean),
  );

  // Parse product IDs and names from URL (format: "id1:name1,id2:name2")
  const parsedProducts = useMemo(
    () =>
      (searchParams.get("productIds") || "")
        .split(",")
        .filter(Boolean)
        .map((item) => {
          const [id, name] = item.split(":");

          return id && name ? { id, name } : null;
        })
        .filter((item): item is { id: string; name: string } => item !== null),
    [searchParams],
  );

  const [linkedProductIds, setLinkedProductIds] = useState<string[]>(() =>
    (searchParams.get("productIds") || "")
      .split(",")
      .filter(Boolean)
      .map((item) => item.split(":")[0])
      .filter(Boolean),
  );

  // Selection state for bulk association/disassociation
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Map<string, ScrapedProduct>
  >(new Map());
  const [isBulkAssociateModalOpen, setIsBulkAssociateModalOpen] =
    useState(false);
  const [isBulkDisassociateModalOpen, setIsBulkDisassociateModalOpen] =
    useState(false);

  // Sync IDs from URL when it changes
  useEffect(() => {
    const categoryIdsFromUrl = parsedCategories.map((c) => c.id);

    setScrapedCategoryIds(categoryIdsFromUrl);
  }, [parsedCategories]);

  useEffect(() => {
    const brandIdsFromUrl = parsedBrands.map((b) => b.id);

    setScrapedBrandIds(brandIdsFromUrl);
  }, [parsedBrands]);

  useEffect(() => {
    const productIdsFromUrl = parsedProducts.map((p) => p.id);

    setLinkedProductIds(productIdsFromUrl);
  }, [parsedProducts]);

  const { data: scrapedProducts, isFetching: isScrapedProductsLoading } =
    useGetScrapedProducts({
      ...pageParams,
      name: debouncedNameFilter || undefined,
      supplierIds: supplierIdFilter || undefined,
      inStock:
        stockFilter === "in-stock"
          ? true
          : stockFilter === "out-of-stock"
            ? false
            : undefined,
      withoutSupplierCategory:
        categoryFilter === "without-category"
          ? true
          : categoryFilter === "with-category"
            ? false
            : undefined,
      withoutSupplierBrand:
        brandFilter === "without-brand"
          ? true
          : brandFilter === "with-brand"
            ? false
            : undefined,
      hasLinkedProduct:
        linkedProductStatus === "linked"
          ? true
          : linkedProductStatus === "not-linked"
            ? false
            : undefined,
      supplierCategoryIds:
        scrapedCategoryIds.length > 0
          ? scrapedCategoryIds.join(",")
          : undefined,
      supplierBrandIds:
        scrapedBrandIds.length > 0 ? scrapedBrandIds.join(",") : undefined,
      blacklistOnly:
        blacklistFilter === "blacklisted"
          ? true
          : blacklistFilter === "whitelisted"
            ? false
            : undefined,
      productIds:
        linkedProductIds.length > 0 ? linkedProductIds.join(",") : undefined,
    });

  useEffect(() => {
    const urlPage = searchParams.get("page");
    const urlLimit = searchParams.get("limit");

    if (urlPage) {
      const page = parseInt(urlPage);

      setPageParams((prev) => ({ ...prev, page }));
    }

    if (urlLimit) {
      const limit = parseInt(urlLimit);

      setPageParams((prev) => ({ ...prev, limit }));
    }
  }, [searchParams]);

  const handlePageChange = (newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleLimitChange = (newLimit: number) => {
    searchParams.set("limit", newLimit.toString());
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleNameChange = (newName: string) => {
    setNameFilter(newName);
    if (newName) {
      searchParams.set("name", newName);
    } else {
      searchParams.delete("name");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleSupplierIdChange = (newSupplierId: string) => {
    setSupplierIdFilter(newSupplierId);
    if (newSupplierId) {
      searchParams.set("supplierId", newSupplierId);
    } else {
      searchParams.delete("supplierId");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleBrandFilterChange = (value: string) => {
    setBrandFilter(value);
    if (value && value !== "all") {
      searchParams.set("brandFilter", value);
    } else {
      searchParams.delete("brandFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleCategoryFilterChange = (value: string) => {
    setCategoryFilter(value);
    if (value && value !== "all") {
      searchParams.set("categoryFilter", value);
    } else {
      searchParams.delete("categoryFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleStockFilterChange = (value: string) => {
    setStockFilter(value);
    if (value && value !== "all") {
      searchParams.set("stockFilter", value);
    } else {
      searchParams.delete("stockFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleBlacklistFilterChange = (value: string) => {
    setBlacklistFilter(value);
    if (value && value !== "all") {
      searchParams.set("blacklistFilter", value);
    } else {
      searchParams.delete("blacklistFilter");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleLinkedProductStatusChange = (value: string) => {
    setLinkedProductStatus(value);
    if (value && value !== "all") {
      searchParams.set("linkedStatus", value);
    } else {
      searchParams.delete("linkedStatus");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleScrapedCategoryIdsChange = (
    ids: string[],
    items: { id: string; name: string }[],
  ) => {
    setScrapedCategoryIds(ids);
    if (ids.length > 0) {
      // Store as "id1:name1,id2:name2"
      const serialized = items
        .map((item) => `${item.id}:${item.name}`)
        .join(",");

      searchParams.set("categoryIds", serialized);
    } else {
      searchParams.delete("categoryIds");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleScrapedBrandIdsChange = (
    ids: string[],
    items: { id: string; name: string }[],
  ) => {
    setScrapedBrandIds(ids);
    if (ids.length > 0) {
      // Store as "id1:name1,id2:name2"
      const serialized = items
        .map((item) => `${item.id}:${item.name}`)
        .join(",");

      searchParams.set("brandIds", serialized);
    } else {
      searchParams.delete("brandIds");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleLinkedProductIdsChange = (
    ids: string[],
    items: { id: string; name: string }[],
  ) => {
    setLinkedProductIds(ids);
    if (ids.length > 0) {
      // Store as "id1:name1,id2:name2"
      const serialized = items
        .map((item) => `${item.id}:${item.name}`)
        .join(",");

      searchParams.set("productIds", serialized);
    } else {
      searchParams.delete("productIds");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
  };

  const handleSelectionChange = (
    keys: "all" | Set<string | number>,
    currentItems: ScrapedProduct[],
  ) => {
    if (keys === "all") {
      // Add all current page items
      const newMap = new Map(selectedProductsMap);

      currentItems.forEach((item) => {
        selectedProductIds.add(item.id);
        newMap.set(item.id, item);
      });
      setSelectedProductIds(new Set(selectedProductIds));
      setSelectedProductsMap(newMap);
    } else {
      const keySet = new Set(keys as Set<string>);
      const newIds = new Set<string>();
      const newMap = new Map<string, ScrapedProduct>();

      // Keep selections from other pages (not on current page)
      selectedProductsMap.forEach((product, id) => {
        if (!currentItems.find((item) => item.id === id)) {
          newIds.add(id);
          newMap.set(id, product);
        }
      });

      // Add/update current page selections
      currentItems.forEach((item) => {
        if (keySet.has(item.id)) {
          newIds.add(item.id);
          newMap.set(item.id, item);
        }
      });

      setSelectedProductIds(newIds);
      setSelectedProductsMap(newMap);
    }
  };

  const handleBulkAssociate = () => {
    setIsBulkAssociateModalOpen(true);
  };

  const handleBulkAssociateClose = () => {
    setIsBulkAssociateModalOpen(false);
    // Clear selections on close
    setSelectedProductIds(new Set());
    setSelectedProductsMap(new Map());
  };

  const handleBulkDisassociate = () => {
    setIsBulkDisassociateModalOpen(true);
  };

  const handleBulkDisassociateClose = () => {
    setIsBulkDisassociateModalOpen(false);
    // Clear selections on close
    setSelectedProductIds(new Set());
    setSelectedProductsMap(new Map());
  };

  // Clear selections when filters change
  useEffect(() => {
    setSelectedProductIds(new Set());
    setSelectedProductsMap(new Map());
  }, [
    debouncedNameFilter,
    supplierIdFilter,
    brandFilter,
    categoryFilter,
    stockFilter,
    blacklistFilter,
    linkedProductStatus,
    scrapedCategoryIds.join(","),
    scrapedBrandIds.join(","),
    linkedProductIds.join(","),
  ]);

  return (
    <AppPage
      subtitle={
        scrapedProducts ? (
          <>
            <span className="tabular-nums">
              {scrapedProducts.total.toLocaleString()}
            </span>{" "}
            products scraped from suppliers
          </>
        ) : (
          "Manage and review products scraped from suppliers"
        )
      }
      title="Scraped products"
    >
      <ScrapedProductTable
        blacklistFilter={blacklistFilter}
        brandFilter={brandFilter}
        categoryFilter={categoryFilter}
        changePage={handlePageChange}
        handleBlacklistFilterChange={handleBlacklistFilterChange}
        handleBrandFilterChange={handleBrandFilterChange}
        handleCategoryFilterChange={handleCategoryFilterChange}
        handleLimitChange={handleLimitChange}
        handleLinkedProductIdsChange={handleLinkedProductIdsChange}
        handleLinkedProductStatusChange={handleLinkedProductStatusChange}
        handleScrapedBrandIdsChange={handleScrapedBrandIdsChange}
        handleScrapedCategoryIdsChange={handleScrapedCategoryIdsChange}
        handleSearchTermChange={handleNameChange}
        handleStockFilterChange={handleStockFilterChange}
        handleSupplierIdChange={handleSupplierIdChange}
        isLoading={isScrapedProductsLoading}
        items={scrapedProducts?.data ?? []}
        limit={pageParams.limit ?? 10}
        linkedProductIds={linkedProductIds}
        linkedProductInitialItems={parsedProducts}
        linkedProductStatus={linkedProductStatus}
        page={pageParams.page ?? 1}
        scrapedBrandIds={scrapedBrandIds}
        scrapedBrandInitialItems={parsedBrands}
        scrapedCategoryIds={scrapedCategoryIds}
        scrapedCategoryInitialItems={parsedCategories}
        searchTerm={nameFilter}
        selectedProductIds={selectedProductIds}
        selectedProductsMap={selectedProductsMap}
        stockFilter={stockFilter}
        supplierId={supplierIdFilter}
        totalPage={scrapedProducts?.totalPages ?? 1}
        totalProducts={scrapedProducts?.total ?? 0}
        onBulkAssociate={handleBulkAssociate}
        onBulkDisassociate={handleBulkDisassociate}
        onSelectionChange={handleSelectionChange}
      />

      {isBulkAssociateModalOpen && selectedProductIds.size > 0 && (
        <AssociateProductModal
          isOpen={isBulkAssociateModalOpen}
          scrapedProducts={Array.from(selectedProductsMap.values()).filter(
            (product) => !product.product,
          )}
          onClose={handleBulkAssociateClose}
        />
      )}

      {isBulkDisassociateModalOpen && selectedProductIds.size > 0 && (
        <DisassociateProductModal
          isOpen={isBulkDisassociateModalOpen}
          scrapedProducts={Array.from(selectedProductsMap.values()).filter(
            (product) => product.product !== null,
          )}
          onClose={handleBulkDisassociateClose}
        />
      )}
    </AppPage>
  );
};
